import { Package, TramFront, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TransportMode } from '~/hooks/useTransportState';

interface TransportLoadsBadgeProps {
  transport: TransportMode;
  loads: number;
  vehicleName?: string;
  testId?: string;
}

export function TransportLoadsBadge({
  transport,
  loads,
  vehicleName,
  testId,
}: Readonly<TransportLoadsBadgeProps>) {
  const { t } = useTranslation('cart');

  const configs = {
    truck: {
      icon: (
        <Truck className="w-3 h-3 text-status-info-600" aria-hidden="true" />
      ),
      label: t('cart.trucks_lowercase'),
      wrapper:
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-status-info-200 ring-1 ring-status-info-600',
      textColor: 'text-status-info-600',
      numColor: 'text-xs font-semibold text-status-info-600',
    },
    railcar: {
      icon: (
        <TramFront
          className="w-3 h-3 text-status-label-600"
          aria-hidden="true"
        />
      ),
      label: t('cart.railcars_lowercase'),
      wrapper:
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-status-label-100 ring-1 ring-status-label-600',
      textColor: 'text-status-label-600',
      numColor: 'text-xs font-semibold text-status-label-700',
    },
    container: {
      icon: <Package className="w-3 h-3 text-warning-600" aria-hidden="true" />,
      label: t('cart.containers_lowercase'),
      wrapper:
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-warning-50 ring-1 ring-warning-600',
      textColor: 'text-warning-600',
      numColor: 'text-xs font-semibold text-warning-700',
    },
  } as const;

  const { icon, label, wrapper, textColor, numColor } = configs[transport];

  return (
    <div className={wrapper}>
      {icon}
      <span data-testid={testId} className={numColor}>
        {loads.toFixed(2)}
      </span>
      <span className={`text-xs ${textColor}`}>
        {label}
        {vehicleName && <> ({vehicleName})</>}
      </span>
    </div>
  );
}
